import { Check, ChevronDown, Search, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { cn } from "./cn";

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Shown beside the label — a project key, a person's role. */
  hint?: string;
}

/**
 * A filter that accepts several answers.
 *
 * <p>Nothing chosen means "do not filter on this", which is why the closed control reads "Any"
 * rather than "None". The two are opposites, and a filter that says None while matching everything
 * is the kind of wrong that goes unnoticed for months.
 *
 * <p>Checkboxes rather than a combobox. Most of these lists are short and known — eight statuses,
 * four priorities — and typing to filter a list of four is slower than reading it.
 *
 * <p>The lists that are neither short nor known get {@code searchable}. Four hundred employees is
 * not a list anybody reads, and scrolling past two hundred names is the difference between a
 * filter people use and one they avoid.
 */
export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  emptyLabel = "Any",
  disabled = false,
  searchable = false,
}: {
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  emptyLabel?: string;
  disabled?: boolean;
  /** Adds a type-to-filter box. For lists nobody reads top to bottom. */
  searchable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const container = useRef<HTMLDivElement>(null);
  const listId = useId();

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        // Stopped here so a filter popover closes without also closing whatever is behind it.
        event.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setFilter("");
    }
  }, [open]);

  const toggle = (value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter((each) => each !== value)
        : [...selected, value],
    );
  };

  const chosen = options.filter((option) => selected.includes(option.value));

  /*
   * Matches the hint as well as the label, because a project key or an employee code is often what
   * somebody actually remembers — "MB" finds Mobile Banking.
   */
  const needle = filter.trim().toLowerCase();
  const visible = needle === ""
    ? options
    : options.filter((option) =>
        option.label.toLowerCase().includes(needle)
        || (option.hint?.toLowerCase().includes(needle) ?? false));
  const summary =
    chosen.length === 0
      ? emptyLabel
      : chosen.length === 1
        ? chosen[0]!.label
        : `${chosen.length} selected`;

  return (
    <div className="relative" ref={container}>
      <label className="mb-1 block text-xs font-medium text-ink-600">{label}</label>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        className={cn(
          "flex h-9 w-full items-center gap-2 rounded-lg border px-3 text-left text-sm transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400",
          "disabled:cursor-not-allowed disabled:bg-ink-50 disabled:text-ink-400",
          chosen.length > 0
            ? "border-brand-400 bg-brand-50 text-brand-700"
            : "border-ink-200 bg-surface text-ink-700 hover:border-ink-300",
        )}
      >
        <span className="min-w-0 flex-1 truncate">{summary}</span>
        {chosen.length > 0 && (
          <span
            role="button"
            tabIndex={0}
            aria-label={`Clear ${label}`}
            onClick={(event) => {
              event.stopPropagation();
              onChange([]);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                event.stopPropagation();
                onChange([]);
              }
            }}
            className="shrink-0 rounded p-0.5 hover:bg-brand-100"
          >
            <X className="h-3.5 w-3.5" />
          </span>
        )}
        <ChevronDown
          className={cn("h-4 w-4 shrink-0 transition-transform", open && "rotate-180")}
        />
      </button>

      {open && (
        <div
          id={listId}
          className="absolute z-30 mt-1 w-full min-w-56 rounded-lg border border-ink-200 bg-surface shadow-lg"
        >
          {searchable && (
            /* Sticky rather than scrolling away: the box is how you got to the bottom of a long
               list, and losing it there means scrolling back up to change one letter. */
            <div className="relative border-b border-ink-100 p-2">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-400" />
              <input
                autoFocus
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                placeholder={`Search ${label.toLowerCase()}…`}
                aria-label={`Search ${label}`}
                className="h-8 w-full rounded-md border border-ink-200 bg-surface pl-7 pr-2 text-sm text-ink-900 placeholder:text-ink-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
              />
            </div>
          )}

          <div role="listbox" aria-multiselectable className="max-h-64 overflow-y-auto py-1">
            {options.length === 0 ? (
              <p className="px-3 py-2 text-sm text-ink-500">Nothing to choose from.</p>
            ) : visible.length === 0 ? (
              <p className="px-3 py-2 text-sm text-ink-500">
                Nothing matches “{filter.trim()}”.
              </p>
            ) : (
              visible.map((option) => {
                const active = selected.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => toggle(option.value)}
                    className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm transition-colors hover:bg-ink-50"
                  >
                    <span
                      className={cn(
                        "grid h-4 w-4 shrink-0 place-items-center rounded border",
                        active
                          ? "border-brand-600 bg-brand-600 text-white"
                          : "border-ink-300 bg-surface",
                      )}
                    >
                      {active && <Check className="h-3 w-3" strokeWidth={3} />}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-ink-900">{option.label}</span>
                    {option.hint && (
                      <span className="shrink-0 font-mono text-[11px] text-ink-500">
                        {option.hint}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
